import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';

const PriceChart = ({ selectedTimeframe, activeIndicators, selectedAsset }) => {
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Mock data generation based on timeframe and asset
  useEffect(() => {
    setIsLoading(true);
    
    const generateMockData = () => {
      const dataPoints = selectedTimeframe === '1m' ? 100 : selectedTimeframe === '1d' ? 30 : 50;
      const basePrice = selectedAsset === 'EUA' ? 85.50 : 78.25;
      const data = [];
      
      for (let i = 0; i < dataPoints; i++) {
        const timestamp = new Date(Date.now() - (dataPoints - i) * (selectedTimeframe === '1m' ? 60000 : selectedTimeframe === '1h' ? 3600000 : 86400000));
        const price = basePrice + (Math.random() - 0.5) * 10 + Math.sin(i / 10) * 5;
        const volume = Math.floor(Math.random() * 1000000) + 500000;
        
        data?.push({
          timestamp: timestamp?.toISOString(),
          time: selectedTimeframe === '1m' ? timestamp?.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) :
                selectedTimeframe === '1h' ? timestamp?.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) :
                timestamp?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          price: parseFloat(price?.toFixed(2)),
          volume: volume,
          sma: activeIndicators?.includes('sma') ? parseFloat((price * 0.98)?.toFixed(2)) : null,
          ema: activeIndicators?.includes('ema') ? parseFloat((price * 0.99)?.toFixed(2)) : null,
          rsi: activeIndicators?.includes('rsi') ? Math.floor(Math.random() * 40) + 30 : null,
          high: parseFloat((price * 1.02)?.toFixed(2)),
          low: parseFloat((price * 0.98)?.toFixed(2)),
          open: parseFloat((price * 0.995)?.toFixed(2)),
          close: parseFloat(price?.toFixed(2))
        });
      }
      
      return data;
    };

    setTimeout(() => {
      setChartData(generateMockData());
      setIsLoading(false);
    }, 500);
  }, [selectedTimeframe, activeIndicators, selectedAsset]);

  const formatTooltipValue = (value, name) => {
    if (name === 'volume') {
      return [new Intl.NumberFormat()?.format(value), 'Volume'];
    }
    if (name === 'rsi') {
      return [value, 'RSI'];
    }
    return [`€${value}`, name?.toUpperCase()];
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 h-96 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin">
            <Icon name="Loader2" size={24} className="text-primary" />
          </div>
          <span className="text-muted-foreground">Loading chart data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-foreground">
            {selectedAsset} Price Chart
          </h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Clock" size={16} />
            <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-2xl font-bold text-foreground">
              €{chartData?.[chartData?.length - 1]?.price || '0.00'}
            </div>
            <div className="text-sm text-success flex items-center gap-1">
              <Icon name="TrendingUp" size={14} />
              +2.34% (+€1.89)
            </div>
          </div>
        </div>
      </div>
      <div className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `€${value}`}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'var(--color-popover)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                color: 'var(--color-popover-foreground)'
              }}
              formatter={formatTooltipValue}
            />
            <Legend />
            
            <Line 
              type="monotone" 
              dataKey="price" 
              stroke="var(--color-primary)" 
              strokeWidth={2}
              dot={false}
              name="Price"
            />
            
            {activeIndicators?.includes('sma') && (
              <Line 
                type="monotone" 
                dataKey="sma" 
                stroke="var(--color-warning)" 
                strokeWidth={1}
                strokeDasharray="5 5"
                dot={false}
                name="SMA"
              />
            )}
            
            {activeIndicators?.includes('ema') && (
              <Line 
                type="monotone" 
                dataKey="ema" 
                stroke="var(--color-accent)" 
                strokeWidth={1}
                strokeDasharray="3 3"
                dot={false}
                name="EMA"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* Volume Chart */}
      {activeIndicators?.includes('volume') && (
        <div className="mt-6 h-32">
          <h4 className="text-sm font-medium text-foreground mb-2">Volume</h4>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="time" 
                stroke="var(--color-muted-foreground)"
                fontSize={10}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                fontSize={10}
                tickFormatter={(value) => `${(value / 1000000)?.toFixed(1)}M`}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-popover)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-popover-foreground)'
                }}
                formatter={formatTooltipValue}
              />
              <Area 
                type="monotone" 
                dataKey="volume" 
                stroke="var(--color-secondary)" 
                fill="var(--color-secondary)"
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PriceChart;